import { ROLE_ORDER } from '@sdk';
import { t } from '@lingui/core/macro';
import {
  ActionInfo,
  ComputeNode,
  type ComputeNodeSize,
  ComputeProviderType,
  dataContext,
  dataManager,
  ExecutionEnvironmentStatus,
  type GitOrigin,
  type HubRepoOrigin,
  isApiError,
  type NodeStatus,
  type ProvisionResult,
  type ProvisionSetup,
  type ProvisionStep,
  QueryRequest,
  SANDBOX_PROVIDERS,
  TypeId,
  WORKSPACE_FLAVOR,
} from '@sdk';
import { useAuth, useEntitiesQuery } from '@sdk/react/hooks';
import { type ContentInstallSpec, installProjectLandingUrl } from '@src/lib/content-install';
import { errorMessage } from '@src/lib/error-message';
import { type BulkResult, runLimited } from '@src/lib/run-limited';
import { type BulkAction, bulkActionCopy, bulkSummary } from '@src/pages/hub-home/machines-bulk';
import { notify } from '@src/notifications';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Sandboxes = cloud FlowPad instances running on the Hub-selected provider — a
 * ComputeNode whose `node_config.flavor === 'workspace'`. This hook lists them
 * and drives the hub's EXISTING ComputeNode API to create/terminate them.
 *
 * Creating, launching and opening are THREE separate operations:
 *   createSandbox: save (workspace-scoped). Nothing is provisioned.
 *   launchSandbox: ops/setup → ops/workspace-ready → project setup
 *   openSandbox:   navigate to open/system
 *
 * The create/launch split is the reason a box can exist without costing
 * anything: `ops/setup` is what boots a billable VM, so a create that runs it
 * charges for a machine the user may not open today. A created-but-unlaunched
 * node has no `node_provider_id`, which is the SAME thing the hub tests for —
 * `open` answers "this machine has not been set up yet" for exactly this
 * node — so the two ends agree on what an unlaunched box is without a second
 * marker to keep in sync.
 *
 * What the first launch should set up travels in `node_config.pending_setup`,
 * written at create time. It has to be persisted rather than held in the
 * dialog's state: creating with a project picked and launching from the card
 * three days later must still get that project, and client state does not
 * survive the Done click.
 *
 * Creating and opening used to be one call that ended in `window.open`, which
 * forced the whole pipeline to run inside a single click gesture with a
 * placeholder tab claimed up front so a popup blocker wouldn't eat it. Splitting
 * them means the open is its own gesture, so the tab is claimed with its real
 * URL and the placeholder document, its progress painting, and the
 * close-on-error path are all gone.
 *
 * Only meaningful against a hub backend: the `workspace` flavor + `workspace-ready`
 * op live in the hub, not the local desktop backend.
 */

/** The hub's name for the box's own UI: its `system` DOOR. The client names a door and
 *  never a port — the hub resolves the placement's row for it, asks the policy whether
 *  THIS caller may enter, and hands the browser a ticket. This is the only way anything
 *  here opens a box.
 *  Wire contract — pinned hub-side by `unit/test_open_service_route_contract.py`. */
export const WORKSPACE_SERVICE = 'system';
// `WORKSPACE_FLAVOR` is imported from the SDK: this hook WRITES the marker at
// create time and `ComputeNode.isSandbox` READS it, so a local copy would let the
// writer and the reader drift apart silently.

// Step/StepStatus are the shared checklist model (`@src/hooks/use-step-flow`),
// re-exported here so existing importers of this module keep working. This
// hook predates that extraction and still drives its own steps inline, because
// it also mirrors each transition into the claimed tab's placeholder document
// (see paintTab) — something the generic runner has no concept of.
import type { Step as GenericStep } from './use-step-flow';

export type StepId =
  | 'launch'
  | 'health'
  | 'validate'
  | 'clone'
  | 'init'
  | 'index'
  | 'companion'
  | 'context'
  | 'default'
  | 'open';
export type Step = GenericStep<StepId>;

/** Every row from `validate` to `default` is one `computeNodeTools` command, so
 *  a row finishing IS a command returning — the checklist needs no separate
 *  progress channel. Which rows appear is decided by {@link plannedSteps}. */
const STEP_LABELS: Record<StepId, string> = {
  launch: 'Starting the sandbox',
  health: 'Starting FlowPad and signing in inside the sandbox',
  validate: 'Checking the project name',
  clone: 'Cloning the repository',
  init: 'Setting up the project',
  index: 'Indexing the project',
  companion: 'Setting up the controller project',
  context: 'Adding dependencies',
  default: 'Choosing the project to open',
  open: 'Finishing up',
};

/**
 * Could this setup have dependencies to add?
 *
 * A git-backed project can declare them in its manifest, which only the clone
 * can reveal — so the answer is "maybe" for anything with a repo, and the step
 * reports "none declared" when it turns out there were none. Shared by the row
 * planner and the runner so a row can never be planned without its step, or a
 * step run without its row.
 */
function hasContextWork(setup: SandboxSetup): boolean {
  // A manifest (and the install that reconciles one) can only come from a repo.
  return Boolean(setup.gitOrigin || setup.contextProjects?.length);
}

/**
 * The rows for THIS launch, in the order they will run.
 *
 * Derived rather than declared: a launch is a sequence of computeNodeTools
 * calls, and which calls happen depends on what the user asked for. A fixed
 * list would show a "Cloning" row for a project with no repository, and every
 * new flow would hand-maintain its own copy.
 */
/**
 * The project half of a launch, for a box whose VM already exists.
 *
 * `launch` and `health` are deliberately absent: they have already succeeded, and
 * showing them idle would report work that is not going to happen.
 */
function plannedProjectSteps(setup: SandboxSetup): Step[] {
  // No repo to fetch → the box mounts the project empty instead, and an empty
  // directory has nothing to index.
  const ids: StepId[] = setup.gitOrigin ? (['validate', 'clone', 'index'] as StepId[]) : (['init'] as StepId[]);
  if (setup.companions?.length) ids.push('companion');
  if (hasContextWork(setup)) ids.push('context');
  ids.push('default', 'open');
  return ids.map((id) => ({ id, label: STEP_LABELS[id], status: 'idle' }));
}

/** Exported so a caller that hasn't started a launch yet — `LaunchLanding`,
 *  building a full sign-in→setup→open preview before `createSandbox` has run —
 *  can render the SAME row set this hook would drive, instead of a placeholder
 *  that has to be kept in sync with it by hand. */
export function plannedSteps(setup?: SandboxSetup): Step[] {
  const step = (id: StepId): Step => ({ id, label: STEP_LABELS[id], status: 'idle' });
  // The project rows end in `open`; with no project there is only the box to open.
  return [step('launch'), step('health'), ...(setup ? plannedProjectSteps(setup) : [step('open')])];
}

/**
 * Live per-sandbox info from `ops/status`.
 *
 * An alias, not a second definition: the shape is normalized server-side and
 * declared once in the SDK as `NodeStatus`. It used to be re-typed here, which
 * is how it drifted into being one provider's field set unioned with another's.
 */
export type SandboxDetails = NodeStatus;

/**
 * The OSS↔hub wire-field divergence lives ONLY in these three helpers. The hub's
 * ComputeNode field is `node_provider`, but the OSS entity types it
 * `node_provider_type` and drops the former on `toJSON()`. `readProvider`
 * tolerates both on read; `hubEntityJson` re-injects it on write. A fresh draft
 * uses the Hub's bootstrap-selected provider so `ops/setup` never silently
 * ignores an operator's provider selection.
 *
 * The E2B fallback is not just for older hubs: `default_compute_provider` is
 * hub-only, so it is absent whenever these screens are served by a local or
 * desktop backend rather than the hub — which they are, since the local server
 * declares `supported_pages: ["desk","hub"]`. It is also what answers a hub
 * whose default is `local_machine`, a provider that hosts compute nodes but
 * never a sandbox.
 */
function readProvider(node: ComputeNode): string | undefined {
  return (node as unknown as { node_provider?: string }).node_provider ?? node.node_provider_type;
}

/** A machine a person connected with `flow connect` — not a cloud sandbox, so no provider gates it. */
export function isUserMachine(node: ComputeNode): boolean {
  return readProvider(node) === ComputeProviderType.USER_MACHINE;
}

function defaultSandboxProvider(): ComputeProviderType {
  // Validated against the SANDBOX providers, not against every provider: a hub
  // configured for `local_machine` would otherwise be taken at its word and mint
  // a node that `isSandbox` — reading the same set — never recognizes as a sandbox.
  const configured: ComputeProviderType | undefined = dataContext.bootstrapInfo?.default_compute_provider;
  return configured && SANDBOX_PROVIDERS.has(configured) ? configured : ComputeProviderType.E2B;
}

function hubEntityJson(node: ComputeNode, patch: Record<string, unknown> = {}): Record<string, unknown> {
  return { ...node.toJSON(), node_provider: readProvider(node) ?? defaultSandboxProvider(), ...patch };
}

/** How often a connected machine's card re-reads its status (it can connect or drop at any time). */
export const MACHINE_STATUS_POLL_MS = 5_000;

/**
 * The absolute URL that opens a sandbox's workspace app: `compute_node/<id>/open/system`.
 *
 * The hub owns readiness and authorization: it admits the caller, resumes a paused box,
 * waits for the workspace to answer, and only then redirects with a ticket. The client
 * names the DOOR and never a port.
 *
 * Exported so the wire-contract test asserts against the URL production
 * actually navigates to, rather than re-deriving one from copied literals.
 */
export function workspaceServiceUrl(nodeId: string, next?: string): string {
  const info = new ActionInfo('open', ComputeNode.type, nodeId, 'GET');
  info.subpath = WORKSPACE_SERVICE;
  // `next`: the app path the box opens on once it is up — a launch's `action=launch` link.
  // The hub accepts only a same-origin path and carries it through its sign-in gate.
  if (next) info.queryParameters = { ...(info.queryParameters ?? {}), next };
  return info.fullActionUrl;
}

/**
 * Has this box ever been launched?
 *
 * `node_provider_id` is set by `ops/setup` and by nothing else, so its absence
 * means no VM was ever provisioned for this node. Deliberately not a flag of our
 * own: the hub reaches the same verdict from the same field (`_open_service_op`
 * refuses a node without one), and a second marker could only ever disagree.
 */
export function isLaunched(node: ComputeNode): boolean {
  return !!node.node_provider_id;
}

/** Where a not-yet-launched box carries what its first launch should set up. */
const PENDING_SETUP_KEY = 'pending_setup';

/**
 * The setup a created box is still owed, if any.
 *
 * Untrusted in the type sense — it is whatever was written into the config blob
 * — so it is read back through the same shape the writer used and nothing here
 * branches on its contents beyond `plannedSteps` asking what work it implies.
 */
export function pendingSetup(node: ComputeNode): SandboxSetup | undefined {
  const setup = node.node_config?.[PENDING_SETUP_KEY];
  return setup && typeof setup === 'object' ? (setup as SandboxSetup) : undefined;
}

/**
 * Turn a box's auto-login on or off.
 *
 * Its own action, not a PUT on the entity: `update` is granted at editor and
 * above, so on the ordinary write path a shared admin could flip this. The hub
 * keeps `auto_login` in `_immutable_update` to close that path.
 *
 * Lives HERE, next to the launch, because that is what it governs: whether
 * bringing the workspace up signs a person into it. It used to sit in
 * `share-sandbox.ts` — which made it read as a property of sharing, and put the
 * control in the share dialog, where you had to go to change what the next open
 * would do.
 */
export async function setAutoLogin(node: ComputeNode, value: boolean): Promise<boolean> {
  const info = new ActionInfo('auto-login', ComputeNode.type, node.id, 'POST');
  info.hubReflect = true; // the node is hub-owned
  info.bodyParameters = { auto_login: value };
  const res = await dataManager.callAction<Record<string, unknown>, { auto_login: boolean } | undefined>(info);
  return res?.auto_login ?? value;
}

/** Who may sign a box in from a browser. Per box; the hub's `login-access` action. */
export interface LoginAccess {
  allowLogin: boolean;
  roles: string[];
}

/** The roles that mean anything on a machine, highest first (the hub's `LOGIN_ROLE_CHOICES`). */
export const LOGIN_ROLE_CHOICES = ROLE_ORDER.filter(role => role !== 'owner_by_api');

/** What the hub gives a new box: admin and everything above it. */
export const DEFAULT_LOGIN_ACCESS: LoginAccess = { allowLogin: true, roles: ['owner', 'admin'] };

/** A box's current login access, the hub default where it has never been set. */
export function loginAccessOf(node: ComputeNode): LoginAccess {
  return {
    allowLogin: node.allow_login ?? DEFAULT_LOGIN_ACCESS.allowLogin,
    roles: node.login_roles ?? DEFAULT_LOGIN_ACCESS.roles,
  };
}

/**
 * Set who may sign a box in from a browser.
 *
 * Its own owner-only action, like `setAutoLogin` and for the same reason: on the
 * PUT path a shared admin could widen who gets into the box's admin channel.
 */
export async function setLoginAccess(node: ComputeNode, access: LoginAccess): Promise<LoginAccess> {
  const info = new ActionInfo('login-access', ComputeNode.type, node.id, 'POST');
  info.hubReflect = true; // the node is hub-owned
  info.bodyParameters = { allow_login: access.allowLogin, login_roles: access.roles };
  const res = await dataManager.callAction<
    Record<string, unknown>,
    { allow_login: boolean; login_roles: string[] } | undefined
  >(info);
  return res ? { allowLogin: res.allow_login, roles: res.login_roles } : access;
}

function isDefaultLoginAccess(access: LoginAccess): boolean {
  const { allowLogin, roles } = DEFAULT_LOGIN_ACCESS;
  return access.allowLogin === allowLogin && [...access.roles].sort().join() === [...roles].sort().join();
}

/**
 * Next auto-name: one past the highest existing box number, so it reads as latest.
 *
 * Matches the legacy "Desktop N" spelling as well as the current one. Boxes
 * created before the rename are still called "Desktop 3", and a regex that only
 * saw "Sandbox N" would restart the count at 1 and hand the user a "Sandbox 1"
 * sitting under a "Desktop 3" they made yesterday. Existing names are left
 * alone — this reads them, it does not rewrite them.
 */
export function nextSandboxName(sandboxes: ComputeNode[]): string {
  let max = 0;
  for (const d of sandboxes) {
    const m = /^(?:Desktop|Sandbox) (\d+)$/.exec(d.name ?? '');
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return `Sandbox ${max + 1}`;
}

/**
 * The project a freshly created sandbox is set up with — usually the one the
 * user is working on.
 *
 * With a `gitOrigin` the hub clones it (authed when private, which is the only
 * way a private repo is reachable) and copies it into the box — a GitHub-style
 * repo, or the project's hub-hosted repo (`hub_repo`). Without one
 * there is nothing to fetch, so the box mounts it empty: a project that was
 * never cloned from anywhere still gets its directory and its identity.
 */
export interface SandboxSetup {
  /** Absent for a project with no repository behind it. */
  gitOrigin?: GitOrigin | HubRepoOrigin;
  /** Folder/display name for the set-up project. */
  name: string;
  /** Adopted by the box, so one project id spans hub and sandbox. */
  projectId?: string;
  /** Help desks / skills repos to clone and add as dependencies of this project.
   *  Defaults to whatever the cloned repo's own manifest declares. */
  contextProjects?: ContextProject[];
  /** Projects provisioned BESIDE this one, each its own project and never attached — a
   *  launch's controller, checked out next to the target it works on. */
  companions?: SandboxCompanion[];
  /** Review-branch content installation, applied to the hub's checkout before
   *  the tree is copied in, then reconciled on the box. Git-backed only. */
  install?: ContentInstallSpec;
}

/** A project provisioned beside the main one (`SandboxSetup.companions`). */
export interface SandboxCompanion {
  gitOrigin: GitOrigin | HubRepoOrigin;
  name: string;
  /** Adopted by the box, so the launch handler finds it by the hub's id. */
  projectId?: string;
}

/** A repo that becomes its own project on the box AND a dependency of the
 *  main one — how a help desk's skills and assets come into scope. */
export interface ContextProject {
  gitOrigin: GitOrigin;
  name: string;
  /** False: a required dependency, fetched wherever the project opens. */
  optional: boolean;
}

/** Rows the hub reports for one provisioning run, keyed by step. */
type ReportedSteps = Map<ProvisionStep['id'], ProvisionStep>;

/** The steps a failed `provision-project` still reported, when the failure carried them. */
function reportedStepsOf(error: unknown): ProvisionStep[] {
  if (!isApiError(error)) return [];
  const data = (error.response?.data as { data?: { steps?: ProvisionStep[] } } | undefined)?.data;
  return Array.isArray(data?.steps) ? data.steps : [];
}

/** What the hub is asked to set the box up with: the recorded setup, in the hub's spelling. */
function provisionSetupOf(setup: SandboxSetup): ProvisionSetup {
  return {
    name: setup.name,
    ...(setup.projectId ? { project_id: setup.projectId } : {}),
    ...(setup.gitOrigin ? { git_origin: setup.gitOrigin as unknown as Record<string, unknown> } : {}),
    ...(setup.contextProjects
      ? {
          context_projects: setup.contextProjects.map((ctx) => ({
            git_origin: ctx.gitOrigin as unknown as Record<string, unknown>,
            name: ctx.name,
            optional: ctx.optional,
          })),
        }
      : {}),
    ...(setup.companions?.length
      ? {
          companions: setup.companions.map((c) => ({
            git_origin: c.gitOrigin as unknown as Record<string, unknown>,
            name: c.name,
            ...(c.projectId ? { project_id: c.projectId } : {}),
          })),
        }
      : {}),
    ...(setup.install ? { install: setup.install as unknown as Record<string, unknown> } : {}),
  };
}

/** Paint the project rows from what the hub reported. A planned row the hub did not report
 *  succeeded with nothing to do (no context declared); after a failure it never ran. */
function paintProvisionRows(
  rows: ProvisionStep['id'][],
  reported: ReportedSteps,
  patch: PatchStep,
  failed: boolean,
): void {
  for (const id of rows) {
    const step = reported.get(id);
    if (step) {
      patch(id, { status: step.ok ? 'success' : 'error', detail: step.detail || undefined });
    } else if (!failed) {
      patch(id, { status: 'success', detail: id === 'context' ? 'none declared' : undefined });
    } else {
      patch(id, { status: 'idle', detail: undefined });
    }
  }
}

type PatchStep = (id: StepId, next: Partial<Step>) => void;

/**
 * Set the sandbox up with its project and make that the project it opens on.
 *
 * ONE hub call. The box commands underneath (validate, clone, index, attach, default) are the
 * hub's control plane and are not reachable from a browser; the hub runs the sequence and
 * reports each step, which is what paints the checklist rows.
 */
async function provisionSandboxProject(
  node: ComputeNode,
  setup: SandboxSetup,
  patch: PatchStep,
): Promise<ProvisionResult> {
  const rows = plannedProjectSteps(setup)
    .map((s) => s.id)
    .filter((id): id is ProvisionStep['id'] => id !== 'open' && id !== 'launch' && id !== 'health');
  for (const id of rows) patch(id, { status: 'loading', detail: undefined });
  try {
    const result = await node.provisionProject(provisionSetupOf(setup));
    if (!result?.project?.id) throw new Error('Setup did not return a project');
    paintProvisionRows(rows, new Map(result.steps.map((s) => [s.id, s])), patch, false);
    return result;
  } catch (e) {
    const reported: ReportedSteps = new Map(reportedStepsOf(e).map((s) => [s.id, s]));
    if (![...reported.values()].some((s) => !s.ok)) {
      // The failure named no step: blame the first row that did not report success.
      const first = rows.find((id) => !reported.get(id)?.ok);
      if (first) reported.set(first, { id: first, ok: false, detail: errorMessage(e, 'Setup failed') });
    }
    paintProvisionRows(rows, reported, patch, true);
    throw e;
  }
}

const NO_NODES: ComputeNode[] = [];

export function useSandboxes({ pollMachines = false }: { pollMachines?: boolean } = {}) {
  const { user } = useAuth();

  const sandboxesRequest = useMemo(
    () => new QueryRequest({ type: ComputeNode.type, query: null, scope: [], name: 'useSandboxes-nodes' }),
    [],
  );

  const { data: nodes, isLoading, refetch } = useEntitiesQuery<ComputeNode>(sandboxesRequest, { enabled: !!user });

  // Every machine the user can reach — agent deployment machines carry no workspace
  // flavor, and filtering on it hid all of them. One shared empty list, so the
  // effects keyed on it do not re-run while the query is still loading.
  const sandboxes = nodes ?? NO_NODES;
  // `createSandbox` only needs the list to pick the next auto-name. Reading it
  // through a ref keeps the callback stable across every refetch — including the
  // one it triggers itself — so consumers holding it as a prop don't re-render.
  const sandboxesRef = useRef(sandboxes);
  sandboxesRef.current = sandboxes;

  // ---- live status (probed on load) ----
  const [details, setDetails] = useState<Record<string, SandboxDetails>>({});
  const detailsRef = useRef(details);
  detailsRef.current = details;

  const probeDetails = useCallback(async (node: ComputeNode): Promise<SandboxDetails> => {
    try {
      const res = await node.status();
      return res ?? { status: ExecutionEnvironmentStatus.ERROR };
    } catch {
      return { status: ExecutionEnvironmentStatus.ERROR };
    }
  }, []);

  // Probe these boxes and merge the answers; an unchanged status keeps the same
  // object, so a poll that learns nothing re-renders nothing.
  const probeInto = useCallback(
    async (nodes: ComputeNode[]) => {
      const entries = await Promise.all(nodes.map(async (d) => [d.id, await probeDetails(d)] as const));
      setDetails((prev) => {
        const changed = entries.filter(([id, next]) => {
          const cur = prev[id];
          return (
            !cur || cur.status !== next.status || cur.status_msg !== next.status_msg || cur.hostname !== next.hostname
          );
        });
        return changed.length ? { ...prev, ...Object.fromEntries(changed) } : prev;
      });
    },
    [probeDetails],
  );

  // Probe only machines we haven't seen yet, and forget ones that vanished —
  // re-probing the whole list on every add/delete would be one call per machine.
  useEffect(() => {
    const liveIds = new Set(sandboxes.map((d) => d.id));
    setDetails((prev) => {
      const kept = Object.entries(prev).filter(([id]) => liveIds.has(id));
      return kept.length === Object.keys(prev).length ? prev : Object.fromEntries(kept);
    });
    // Never-launched boxes are skipped, not probed: `ops/status` has no provider
    // id to ask about, so the answer would be an error — painting a card red for
    // a machine that is simply not built yet.
    const fresh = sandboxes.filter((d) => isLaunched(d) && !(d.id in detailsRef.current));
    if (fresh.length) void probeInto(fresh);
  }, [sandboxes, probeInto]);

  // A connected machine comes and goes on its own (`flow connect` started or
  // stopped on it), so the screen showing its card (opt-in: `pollMachines`)
  // re-reads its status. Only while the tab is visible, one probe at a time.
  const userMachines = useMemo(() => sandboxes.filter((d) => isLaunched(d) && isUserMachine(d)), [sandboxes]);
  useEffect(() => {
    if (!pollMachines || !userMachines.length) return;
    let inFlight = false;
    const id = setInterval(() => {
      if (inFlight || document.visibilityState !== 'visible') return;
      inFlight = true;
      void probeInto(userMachines).finally(() => {
        inFlight = false;
      });
    }, MACHINE_STATUS_POLL_MS);
    return () => clearInterval(id);
  }, [pollMachines, userMachines, probeInto]);

  // ---- create / launch ----
  const [steps, setSteps] = useState<Step[]>(() => plannedSteps());
  const [creating, setCreating] = useState(false);
  const [launchingId, setLaunchingId] = useState<string | null>(null);
  const [launchUrl, setLaunchUrl] = useState<string | null>(null);
  const creatingRef = useRef(false);
  const launchingRef = useRef(false);

  const patch = useCallback((id: StepId, next: Partial<Step>) => {
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...next } : s)));
  }, []);

  /**
   * Write the sandbox down. Provisions NOTHING.
   *
   * One save, and the returned node has no `node_provider_id` — there is no VM,
   * no billing, and nothing to open until {@link launchSandbox} runs. What the
   * first launch should set up is persisted on the node itself so it survives
   * the dialog closing.
   *
   * Rejects on failure rather than swallowing, so a caller can leave the dialog
   * open with the error visible.
   */
  const createSandbox = useCallback(
    async (opts?: {
      name?: string;
      size?: ComputeNodeSize;
      sandboxProject?: SandboxSetup;
    }): Promise<ComputeNode | null> => {
      // One create at a time per hook instance: a double-clicked button must not
      // leave two boxes the user then has to find and delete.
      if (creatingRef.current) return null;
      const sandboxProject = opts?.sandboxProject;
      // Prefer the workspace scope (hub does workspace.add_child); fall back to
      // owner scope ([]) when hub mode exposes no workspace — the node is still
      // owned by the caller and listed by the role-scoped query.
      const scope = dataContext.workspaceTypeId ? [dataContext.workspaceTypeId] : [];
      creatingRef.current = true;
      setCreating(true);
      // The rows this box's launch WILL run, all still idle. Planned now because
      // the setup is known now, and a launch from the card has no other source.
      setSteps(plannedSteps(sandboxProject));

      try {
        const draft = new ComputeNode({
          name: opts?.name?.trim() || nextSandboxName(sandboxesRef.current),
          ...(opts?.size ? { node_size: opts.size } : {}),
          node_config: {
            flavor: WORKSPACE_FLAVOR,
            ...(sandboxProject ? { [PENDING_SETUP_KEY]: sandboxProject } : {}),
          },
        });
        await dataManager.save(draft.typeId, scope, hubEntityJson(draft) as never);
        // Best-effort: the list on the page behind is what goes stale, not the node.
        try {
          await refetch();
        } catch {
          // The list is stale until the next refresh. The record is not.
        }
        return draft;
      } finally {
        creatingRef.current = false;
        setCreating(false);
      }
    },
    [refetch],
  );

  /**
   * Boot the box and set its project up. Does NOT open anything.
   *
   * Everything that costs money or takes time lives here: `ops/setup` creates
   * the VM, `workspace-ready` waits for FlowPad inside it, and the project is
   * cloned and indexed afterwards. The caller awaits a node and decides for
   * itself whether to open it, which is what lets the dialog stay on screen and
   * report what happened.
   *
   * `autoLogin` is applied BEFORE the health step, because that step is what
   * signs the box in — flipping the flag after it would leave the very session
   * the user opted out of already running.
   *
   * Rejects on failure; the step rows carry the detail.
   */
  const launchSandbox = useCallback(
    async (
      node: ComputeNode,
      opts?: { sandboxProject?: SandboxSetup; autoLogin?: boolean; loginAccess?: LoginAccess },
    ) => {
      // Same guard as create, and for a stronger reason: a second `ops/setup` on
      // one node overwrites `node_provider_id` and orphans the first VM.
      if (launchingRef.current) return null;
      const sandboxProject = opts?.sandboxProject ?? pendingSetup(node);
      launchingRef.current = true;
      setLaunchingId(node.id);
      setSteps(plannedSteps(sandboxProject));

      const run = async <T>(id: StepId, fn: () => Promise<T>): Promise<T> => {
        patch(id, { status: 'loading', detail: undefined });
        try {
          const result = await fn();
          patch(id, { status: 'success' });
          return result;
        } catch (e) {
          patch(id, { status: 'error', detail: e instanceof Error ? e.message : String(e) });
          throw e;
        }
      };

      try {
        await run('launch', async () => {
          const providerId = await node.setup();
          if (!providerId) throw new Error('setup returned no provider id (provider was dropped)');
          // Only when turning it OFF: `true` is the hub's default for a fresh
          // node, so asking for it again is a round trip that changes nothing.
          if (opts?.autoLogin === false) await setAutoLogin(node, false);
          // Same rule: the hub default needs no round trip.
          if (opts?.loginAccess && !isDefaultLoginAccess(opts.loginAccess)) {
            await setLoginAccess(node, opts.loginAccess);
          }
          return providerId;
        });

        await run('health', async () => {
          const result = await node.workspaceReady();
          if (!result?.healthy) throw new Error('FlowPad did not come up in the sandbox');
          patch('health', {
            detail: result.logged_in ? `signed in as ${result.login_detail}` : 'opened without cloud sign-in',
          });
          // Don't let a failed cloud sign-in pass silently: the sandbox came up, but
          // couldn't reach the hub to sign in (e.g. hub down / WORKSPACE_HUB_URL unset).
          if (!result.logged_in) {
            notify.warning({
              id: 'sandbox-no-signin',
              title: t`Sandbox started without cloud sign-in`,
              message:
                "Couldn't reach the hub to sign this sandbox in — it may be down or unreachable. You can sign in from inside the sandbox.",
            });
          }
          return result;
        });

        // Set the git repo up by composing computeNodeTools, one command per step.
        // The HUB clones (its token is the only one that reaches a private repo)
        // and copies the tree in; the box places, indexes and links it. Runs after
        // the box is up so copy_folder has a target.
        if (sandboxProject) await provisionSandboxProject(node, sandboxProject, patch);

        await run('open', () => {
          // Nothing to resolve any more: the link is derived from the node id, and
          // the hub works out host, port, readiness and gate at click time. The row
          // stays so the checklist still ends on a completed line.
          //
          // `Promise.resolve` rather than `async`: `run` wants a thunk returning a
          // promise, and an `async` body with nothing to await trips
          // `require-await` — which blocks the commit hook on any change to this
          // file. Same value, same timing, no lie about being asynchronous.
          return Promise.resolve(workspaceServiceUrl(node.id));
        });

        // The just-launched sandbox is up — seed its status so the list effect
        // doesn't re-probe it.
        setDetails((s) => ({ ...s, [node.id]: { status: ExecutionEnvironmentStatus.READY } }));
        // Best-effort, and deliberately AFTER the box is usable. The refetch only
        // refreshes the list on the page behind; letting it reject here would
        // report a perfectly good sandbox as a failed launch, and — since the
        // caller opens the box with what this returns — would refuse to open a
        // machine that is already running and already paid for.
        try {
          await refetch();
        } catch {
          // The list is stale until the next refresh. The box is not.
        }
        return node;
      } finally {
        launchingRef.current = false;
        setLaunchingId(null);
      }
    },
    [patch, refetch],
  );

  /**
   * Redo the PROJECT half of a launch on a box that is already up.
   *
   * `launchSandbox` provisions the VM and then sets its project up, and those two
   * fail independently: `ops/setup` succeeding is what makes `isLaunched` true,
   * so a clone that fails immediately after leaves a running box that no launch
   * path will ever revisit — the entry page sees a provisioned node, calls it
   * done, and opens a sandbox holding the box's own starter project instead of
   * the one it was created for.
   *
   * That is exactly the shape of the credential failure: the clone is the FIRST
   * step needing a GitHub token, and by the time the recipient connects one, the
   * machine is already built. Re-running the whole launch is not an option (a
   * second `ops/setup` overwrites `node_provider_id` and orphans the live VM), so
   * the recoverable unit is the project work on its own.
   *
   * Same `provisionSandboxProject` the launch calls, so the two cannot drift.
   * Returns null when there is nothing recorded to set up.
   */
  const provisionProject = useCallback(
    async (node: ComputeNode) => {
      const setup = pendingSetup(node);
      if (!setup) return null;
      if (launchingRef.current) return null;
      launchingRef.current = true;
      setLaunchingId(node.id);
      setSteps(plannedProjectSteps(setup));

      const run = async <T>(id: StepId, fn: () => Promise<T>): Promise<T> => {
        patch(id, { status: 'loading', detail: undefined });
        try {
          const result = await fn();
          patch(id, { status: 'success' });
          return result;
        } catch (e) {
          patch(id, { status: 'error', detail: e instanceof Error ? e.message : String(e) });
          throw e;
        }
      };

      try {
        await provisionSandboxProject(node, setup, patch);
        await run('open', () => Promise.resolve(workspaceServiceUrl(node.id)));
        try {
          await refetch();
        } catch {
          // The list is stale until the next refresh. The project is not.
        }
        return node;
      } finally {
        launchingRef.current = false;
        setLaunchingId(null);
      }
    },
    [patch, refetch],
  );

  // ---- rename ----
  const renameSandbox = useCallback(
    async (node: ComputeNode, name: string) => {
      const trimmed = name.trim();
      if (!trimmed || trimmed === node.name) return;
      await dataManager.save(node.typeId, [], hubEntityJson(node, { name: trimmed }) as never);
      node.markEdit();
      await refetch();
    },
    [refetch],
  );

  // ---- open a sandbox ----
  /**
   * THE way anything here opens a box — a freshly created one and one from the
   * list take the identical path.
   *
   * One navigation to the hub, which owns readiness and authorization: it
   * resumes a paused box and waits for the workspace to answer before
   * redirecting. A client-side probe/resume/navigate sequence cannot — `resume`
   * returning means the VM is back, not that the app is listening.
   *
   * Opened with its final URL inside the click gesture: no blank tab to claim,
   * and no async gap for a popup blocker to catch.
   */
  const openSandbox = useCallback((node: ComputeNode) => {
    window.open(workspaceServiceUrl(node.id), '_blank');
  }, []);

  // ---- delete / terminate ----
  // Per-action sets of busy ids, so a bulk run spins every card it is working on.
  const [deletingIds, markDeleting] = useIdSet();

  // Sign a box out of the cloud. Separate state from `deletingIds` so the two
  // spinners cannot be mistaken for each other on the same card.
  const [loggingOutIds, markLoggingOut] = useIdSet();

  const signOutOne = useCallback(
    async (node: ComputeNode) => {
      markLoggingOut(node.id, true);
      try {
        await node.signOut();
        // Clear the cached field HERE, on the entity, because a refetch cannot:
        // the hub sets `logged_in_user` to null, and the API serializer drops
        // every null field, so the refetched payload simply has no
        // `logged_in_user` key — and the store's `deepAssign` only copies keys
        // the payload carries. The old email therefore survived every refetch
        // and the card kept showing the user as signed in until a page reload
        // built a fresh entity. This is not an optimistic guess: `logout-user`
        // raises unless the box confirmed the sign-out, so reaching this line
        // means the hub already cleared its own copy.
        node.logged_in_user = null;
      } finally {
        markLoggingOut(node.id, false);
      }
    },
    [markLoggingOut],
  );

  const logoutSandbox = useCallback(
    async (node: ComputeNode) => {
      try {
        await signOutOne(node);
        // Still refetch: it is what re-renders the list (a new array ref) and it
        // is the honest source for everything the sign-out may have changed.
        await refetch();
      } catch (err) {
        notify.error({
          id: 'sandbox-logout',
          title: t`Could not sign the sandbox out`,
          message: errorMessage(err, 'The sandbox did not confirm the sign-out.'),
        });
      }
    },
    [refetch, signOutOne],
  );

  // Upgrading the app INSIDE a box. Its own state again, for the same reason:
  // three spinners on one card must not be able to mean each other.
  const [upgradingIds, markUpgrading] = useIdSet();

  const upgradeOne = useCallback(
    async (node: ComputeNode) => {
      markUpgrading(node.id, true);
      try {
        return await node.upgradeApp();
      } finally {
        markUpgrading(node.id, false);
      }
    },
    [markUpgrading],
  );

  /**
   * Upgrade the FlowPad app running inside a box, without signing in to it.
   *
   * `flow stop`, `flow upgrade`, start — the three commands someone would
   * otherwise type into a terminal on the machine — run hub-side as one
   * `ops/upgrade-app` command. Everything about the sequence lives there: the
   * client cannot compose it, because the start has to carry the hub url into
   * the process, and a browser cannot.
   *
   * Slow by nature (a PyPI install plus an app boot), so it reports through
   * notifications rather than a dialog: the card's spinner says which box is
   * busy, and the result says which version it landed on. Deliberately does not
   * throw — every caller is a click handler.
   */
  const upgradeSandbox = useCallback(
    async (node: ComputeNode) => {
      try {
        const result = await upgradeOne(node);
        notify.success({
          id: 'sandbox-upgrade',
          title: t`Sandbox upgraded`,
          message: result?.version
            ? t`${node.name || 'The sandbox'} is now running FlowPad ${result.version}.`
            : t`${node.name || 'The sandbox'} was upgraded and restarted.`,
        });
        // The box may now report a different logged-in user or status, and the
        // list is the honest source for both.
        await refetch();
      } catch (err) {
        notify.error({
          id: 'sandbox-upgrade',
          title: t`Could not upgrade the sandbox`,
          message: errorMessage(err, 'The sandbox did not confirm the upgrade.'),
        });
      }
    },
    [refetch, upgradeOne],
  );

  const deleteOne = useCallback(
    async (node: ComputeNode) => {
      markDeleting(node.id, true);
      try {
        // Shutdown BEFORE delete: it kills the sandbox and revokes the auto-login
        // key (needs the user session). DELETE alone would orphan a live sandbox.
        try {
          await node.shutdown();
        } catch {
          // A node that never finished setup has no sandbox to shut down; delete anyway.
        }
        await dataManager.delete(new TypeId(ComputeNode.type, node.id));
      } finally {
        markDeleting(node.id, false);
      }
    },
    [markDeleting],
  );

  const deleteSandbox = useCallback(
    async (node: ComputeNode) => {
      await deleteOne(node);
      await refetch();
    },
    [deleteOne, refetch],
  );

  /**
   * Run one action on many machines, at most 4 at a time, then refetch once and
   * report the whole run in one notification. Never throws.
   */
  const runBulkAction = useCallback(
    async (action: BulkAction, nodes: ComputeNode[]): Promise<BulkResult<ComputeNode>> => {
      const one = { upgrade: upgradeOne, signout: signOutOne, delete: deleteOne }[action];
      const result = await runLimited(nodes, one);
      try {
        await refetch();
      } catch {
        // The list is stale until the next poll; the outcome below still stands.
      }
      const copy = bulkActionCopy(action);
      const message = bulkSummary(copy.done(result.ok.length, nodes.length), result);
      const id = `sandbox-bulk-${action}`;
      if (result.failed.length) notify.error({ id, title: copy.failed, message });
      else notify.success({ id, title: message });
      return result;
    },
    [refetch, upgradeOne, signOutOne, deleteOne],
  );

  /**
   * Create, launch and open in one call, for the callers that genuinely mean
   * "just get me into a box": the /launch and /install landings and
   * `use-ensure-project`.
   *
   * The hub-home dialog deliberately does NOT use this — it wants the halves
   * apart so it can show the result and let the user choose.
   */
  const launch = useCallback(
    async (opts?: { name?: string; sandboxProject?: SandboxSetup }) => {
      setLaunchUrl(null);
      // Swallows, unlike `createSandbox`. Every caller of this one is
      // fire-and-forget (`void launch(...)`), so a rejection here would surface
      // as an unhandled promise rejection rather than as anything a user sees.
      // The failure is already on screen: the step row carries it. The dialog
      // uses the two halves directly precisely because it CAN await and report.
      let node: ComputeNode | null = null;
      try {
        node = await createSandbox(opts);
        if (node) node = await launchSandbox(node, { sandboxProject: opts?.sandboxProject });
      } catch {
        return null;
      }
      if (!node) return null;
      // Published for the landing pages, which render it as a fallback link when
      // the popup was blocked — the one case where the user needs the URL as
      // something to click rather than something we navigated to.
      setLaunchUrl(workspaceServiceUrl(node.id));
      openSandbox(node);
      return node;
    },
    [createSandbox, launchSandbox, openSandbox],
  );

  return {
    sandboxes,
    isLoading,
    refetch,
    createSandbox,
    creating,
    launchSandbox,
    provisionProject,
    /** Id of the box being launched right now, or null. Per-id so one card's
     *  spinner cannot appear on another's. */
    launchingId,
    launch,
    launchUrl,
    steps,
    openSandbox,
    renameSandbox,
    deleteSandbox,
    /** Ids being deleted / signed out / upgraded right now. Sets, so a bulk run spins every card it touches. */
    deletingIds,
    logoutSandbox,
    loggingOutIds,
    upgradeSandbox,
    upgradingIds,
    runBulkAction,
    details,
  };
}

/** A set of ids held in state, with an add/remove that never loses a concurrent update. */
function useIdSet(): [ReadonlySet<string>, (id: string, on: boolean) => void] {
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set());
  const mark = useCallback((id: string, on: boolean) => {
    setIds((prev) => {
      if (prev.has(id) === on) return prev;
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  return [ids, mark];
}
